import React from 'react';
import type { ViewProps } from 'react-native';
import { theme, VStack } from '@sweetgreen/garnish';

import type { CreditCardOption } from '../../../Checkout.utils';
import type { useCheckoutFieldsValidation } from '../../../hooks';
import { CheckoutLayoutBlock } from '../../CheckoutLayout';
import { CheckoutPaymentBlockAvailableCredit } from './CheckoutPaymentBlock.availableCredit';
import { CheckoutPaymentBlockCardIcons } from './CheckoutPaymentBlock.cardIcons';
import { CheckoutPaymentBlockCreditCardPicker } from './CheckoutPaymentBlock.creditCardPicker';
import { CheckoutPaymentBlockPromoCode } from './CheckoutPaymentBlock.promoCode';

export const CheckoutPaymentBlock = ({
  billingAccountId = 'ADD',
  creditCardOptions,
  useCredit,
  credit,
  availableCredit,
  isSubmittingPromoCode,
  hasPromoCodeError,
  promoCode,
  setPromoCode,
  onCreditCardSelected,
  onAddCardSelected,
  onAvailableCreditSelected,
  onPromoCodeSubmit,
  onPressCreditAdd,
  checkoutFieldsValidation,
  resetCheckoutFieldValidation,
  ...rest
}: CheckoutPaymentBlockProps) => (
  <CheckoutLayoutBlock {...rest}>
    <CheckoutPaymentBlockCardIcons />
    <VStack divider dividerColor={theme.colors.LIGHT} customSpacing={0}>
      <CheckoutPaymentBlockCreditCardPicker
        creditCard={billingAccountId}
        creditCardOptions={creditCardOptions}
        onCreditCardSelected={onCreditCardSelected}
        onAddCardSelected={onAddCardSelected}
        checkoutFieldsValidation={checkoutFieldsValidation}
        resetCheckoutFieldValidation={resetCheckoutFieldValidation}
      />
      <CheckoutPaymentBlockAvailableCredit
        credit={credit}
        availableCredit={availableCredit}
        enabled={useCredit}
        onAvailableCreditSelected={onAvailableCreditSelected}
      />
      <CheckoutPaymentBlockPromoCode
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        isSubmittingPromoCode={isSubmittingPromoCode}
        onPromoCodeSubmit={onPromoCodeSubmit}
        hasPromoCodeError={hasPromoCodeError}
        onPressCreditAdd={onPressCreditAdd}
      />
    </VStack>
  </CheckoutLayoutBlock>
);

type CheckoutPaymentBlockProps = Omit<ViewProps, 'children'> & {
  readonly creditCardOptions: readonly CreditCardOption[];
  readonly useCredit: boolean;
  readonly billingAccountId?: string;
  readonly credit: number;
  readonly availableCredit: number;
  readonly promoCode: string;
  readonly hasPromoCodeError: boolean;
  readonly isSubmittingPromoCode: boolean;
  readonly setPromoCode: (code: string) => void;
  readonly onCreditCardSelected: (billingAccountId: string) => void;
  readonly onAddCardSelected: () => void;
  readonly onAvailableCreditSelected: () => void;
  readonly onPromoCodeSubmit: () => void;
  readonly onPressCreditAdd: () => void;
} & Pick<
    ReturnType<typeof useCheckoutFieldsValidation>,
    'resetCheckoutFieldValidation' | 'checkoutFieldsValidation'
  >;
